import {
  WorkflowContext,
  IWorkflowActions,
  IWorkflowStateDetailActionPayload,
  IWorkflowStateUpdateActionPayload,
  IWorkflowTransitionDetailActionPayload,
  IWorkflowTransitionUpdateActionPayload,
  IWorkflowSetStateActionPayload,
  IWorkflowStateThresholdDetailActionPayload,
  IWorkflowStateThresholdUpdateActionPayload
} from './actions.types';
import { IWorkflowData } from '@/models';

export const actions: IWorkflowActions = {
  fetchWorkflows(context: WorkflowContext): Promise<void> {
    return this.$services.workflow.fetchWorkflows().then(workflows => {
      context.commit('SET_WORKFLOWS', workflows);
    });
  },

  fetchWorkflow(context: WorkflowContext, payload: number): Promise<void> {
    return this.$services.workflow
      .fetchWorkflow(payload)
      .then((workflow: IWorkflowData) => {
        const index = context.state.workflows.findIndex(
          (v: IWorkflowData) => v.id === workflow.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW', workflow);
        } else {
          context.commit('UPDATE_WORKFLOW', workflow);
        }
      });
  },

  createWorkflow(
    context: WorkflowContext,
    payload: IWorkflowData
  ): Promise<number> {
    return this.$services.workflow.createWorkflow(payload).then(workflow => {
      context.commit('ADD_WORKFLOW', workflow);

      return workflow.id!;
    });
  },

  updateWorkflow(
    context: WorkflowContext,
    payload: IWorkflowData
  ): Promise<void> {
    return this.$services.workflow.updateWorkflow(payload).then(workflow => {
      const index = context.state.workflows.findIndex(
        (v: IWorkflowData) => v.id === workflow.id
      );
      if (index === -1) {
        context.commit('ADD_WORKFLOW', workflow);
      } else {
        context.commit('UPDATE_WORKFLOW', workflow);
      }
    });
  },

  deleteWorkflow(context: WorkflowContext, payload: number): Promise<void> {
    return this.$services.workflow.deleteWorkflow(payload).then(() => {
      context.commit('REMOVE_WORKFLOW', payload);
    });
  },

  setWorkflowState(
    context: WorkflowContext,
    payload: IWorkflowSetStateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .setWorkflowState(payload.workflowId, payload.state)
      .then(workflow => {
        const index = context.state.workflows.findIndex(
          (v: IWorkflowData) => v.id === workflow.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW', workflow);
        } else {
          context.commit('UPDATE_WORKFLOW', workflow);
        }
      });
  },

  /** #### STATES #### */
  fetchWorkflowStates(
    context: WorkflowContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflow.fetchWorkflowStates(payload).then(states => {
      context.commit('SET_WORKFLOW_STATES', states);
    });
  },

  fetchWorkflowState(
    context: WorkflowContext,
    payload: IWorkflowStateDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .fetchWorkflowState(payload.workflowId, payload.stateId)
      .then(workflowState => {
        const index = context.state.states.findIndex(
          v => v.id === workflowState.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_STATE', workflowState);
        } else {
          context.commit('UPDATE_WORKFLOW_STATE', workflowState);
        }
      });
  },

  createWorkflowState(
    context: WorkflowContext,
    payload: IWorkflowStateUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .createWorkflowState(payload.workflowId, payload.state)
      .then(workflowState => {
        context.commit('ADD_WORKFLOW_STATE', workflowState);
        context.commit(
          'SET_LAST_UPSERTED_WORKFLOW_STATE_ID',
          workflowState.id!
        );
      });
  },
  updateWorkflowState(
    context: WorkflowContext,
    payload: IWorkflowStateUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .updateWorkflowState(payload.workflowId, payload.state)
      .then(state => {
        const index = context.state.states.findIndex(v => v.id === state.id);
        if (index === -1) {
          context.commit('ADD_WORKFLOW_STATE', state);
        } else {
          context.commit('UPDATE_WORKFLOW_STATE', state);
        }
        context.commit('SET_LAST_UPSERTED_WORKFLOW_STATE_ID', state.id!);
      });
  },
  deleteWorkflowState(
    context: WorkflowContext,
    payload: IWorkflowStateDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .deleteWorkflowState(payload.workflowId, payload.stateId)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_STATE', payload);
      });
  },

  /** #### TRANSITION #### */
  fetchWorkflowTransitions(
    context: WorkflowContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflow
      .fetchWorkflowTransitions(payload)
      .then(transitions => {
        context.commit('SET_WORKFLOW_TRANSITIONS', transitions);
      });
  },

  fetchWorkflowTransition(
    context: WorkflowContext,
    payload: IWorkflowTransitionDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .fetchWorkflowTransition(payload.workflowId, payload.transitionId)
      .then(transition => {
        const index = context.state.transitions.findIndex(
          v => v.id === transition.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_TRANSITION', transition);
        } else {
          context.commit('UPDATE_WORKFLOW_TRANSITION', transition);
        }
      });
  },

  createWorkflowTransition(
    context: WorkflowContext,
    payload: IWorkflowTransitionUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .createWorkflowTransition(payload.workflowId, payload.transition)
      .then(transition => {
        context.commit('ADD_WORKFLOW_TRANSITION', transition);
      });
  },

  updateWorkflowTransition(
    context: WorkflowContext,
    payload: IWorkflowTransitionUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .updateWorkflowTransition(payload.workflowId, payload.transition)
      .then(workflow => {
        const index = context.state.workflows.findIndex(
          (v: IWorkflowData) => v.id === workflow.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_TRANSITION', workflow);
        } else {
          context.commit('UPDATE_WORKFLOW_TRANSITION', workflow);
        }
      });
  },

  deleteWorkflowTransition(
    context: WorkflowContext,
    payload: IWorkflowTransitionDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .deleteWorkflowTransition(payload.workflowId, payload.transitionId)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_TRANSITION', payload);
      });
  },

  /** #### STATE THRESHOLD #### */
  fetchWorkflowStateThresholds(
    context: WorkflowContext,
    payload: IWorkflowStateDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .fetchWorkflowStateThresholds(payload.workflowId, payload.stateId)
      .then(thresholds => {
        context.commit('SET_WORKFLOW_STATE_THRESHOLDS', thresholds);
      });
  },

  fetchWorkflowStateThreshold(
    context: WorkflowContext,
    payload: IWorkflowStateThresholdDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .fetchWorkflowStateThreshold(
        payload.workflowId,
        payload.stateId,
        payload.thresholdId
      )
      .then(threshold => {
        context.commit('UPSERT_WORKFLOW_STATE_THRESHOLD', threshold);
      });
  },

  createWorkflowStateThreshold(
    context: WorkflowContext,
    payload: IWorkflowStateThresholdUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .createWorkflowStateThreshold(
        payload.workflowId,
        payload.stateId,
        payload.threshold
      )
      .then(threshold => {
        context.commit('UPSERT_WORKFLOW_STATE_THRESHOLD', threshold);
      });
  },

  updateWorkflowStateThreshold(
    context: WorkflowContext,
    payload: IWorkflowStateThresholdUpdateActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .updateWorkflowStateThreshold(
        payload.workflowId,
        payload.stateId,
        payload.threshold
      )
      .then(threshold => {
        context.commit('UPSERT_WORKFLOW_STATE_THRESHOLD', threshold);
      });
  },

  deleteWorkflowStateThreshold(
    context: WorkflowContext,
    payload: IWorkflowStateThresholdDetailActionPayload
  ): Promise<void> {
    return this.$services.workflow
      .deleteWorkflowStateThreshold(
        payload.workflowId,
        payload.stateId,
        payload.thresholdId
      )
      .then(() => {
        context.commit('REMOVE_WORKFLOW_STATE_THRESHOLD', payload);
      });
  }
};
